import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PlayerInfo } from '@/components/PlayerInfo'
import type { Player } from '@/lib/types/roster'
import type { TradeOffer, Message } from '@prisma/client'
import { formatDateForDisplay } from '@/lib/utils/date'
import { transformTradeOfferItems } from '@/lib/utils/trade-offers'
import { MessageCircle } from 'lucide-react'
import { useState } from 'react'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { useTeam } from '@/app/lib/providers/team-context'
import { useTradeOffers } from '@/app/lib/hooks/use-trade-offers'
import { AdminModeBanner } from '@/components/ui/admin-mode-banner'

interface TradeOfferDisplayProps {
    tradeOffer: TradeOffer & {
        tradeOfferItems: any[]
        messages: Message[]
        team1: { teamName: string }
        team2: { teamName: string }
    }
    currentTeamId: number
}

export function TradeOfferDisplay({
    tradeOffer: offer,
    currentTeamId
}: TradeOfferDisplayProps) {
    const { adminToggle } = useTeam()
    const {
        acceptTradeOffer,
        rejectTradeOffer,
        withdrawTradeOffer,
        isAccepting,
        isRejecting,
        isWithdrawing
    } = useTradeOffers()
    const [confirmationDialog, setConfirmationDialog] = useState<{
        isOpen: boolean;
        offerId: number | null;
        actionType: 'accept' | 'reject' | 'withdraw' | null;
        teamName: string;
    }>({
        isOpen: false,
        offerId: null,
        actionType: null,
        teamName: '',
    });

    const openConfirmation = (offerId: number, actionType: 'accept' | 'reject' | 'withdraw', teamName?: string) => {
        setConfirmationDialog({
            isOpen: true,
            offerId,
            actionType,
            teamName: teamName || '',
        });
    };

    const handleConfirmation = () => {
        if (!confirmationDialog.offerId || !confirmationDialog.actionType) return;

        if (confirmationDialog.actionType === 'accept') {
            acceptTradeOffer({ offerId: confirmationDialog.offerId, overrideDate: localStorage.getItem('NEXT_PUBLIC_OVERRIDE_DATE') || new Date().toISOString() });
        } else if (confirmationDialog.actionType === 'reject') {
            rejectTradeOffer(confirmationDialog.offerId);
        } else if (confirmationDialog.actionType === 'withdraw') {
            withdrawTradeOffer(confirmationDialog.offerId);
        }

        setConfirmationDialog({
            isOpen: false,
            offerId: null,
            actionType: null,
            teamName: '',
        });
    };

    const formatDate = (date: Date | string | null) => {
        if (!date) return ''
        return formatDateForDisplay(new Date(date))
    }

    const renderTeamSection = (
        teamName: string,
        players: (Player & {
            futureMoney?: Array<{
                year: number
                value: 'all' | 'custom'
                customValue?: number
            }>
        })[],
        picks: Array<{
            year: number
            round: number
            pick?: number | null
            overall?: number | null
            comment?: string
        }>,
        navigationData?: { playerIds: number[]; context: string }
    ) => {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">{teamName} Receives</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-6">
                        {players.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-medium">Players</h3>
                                <div className="space-y-3">
                                    {players.map((player) => (
                                        <div key={player.id} className="p-3 rounded-lg border">
                                            <div className="grid grid-cols-[1fr,auto] gap-4">
                                                <PlayerInfo player={player} navigationData={navigationData} />
                                                {player.futureMoney && player.futureMoney.length > 0 && (
                                                    <div className="space-y-1 text-sm text-muted-foreground border-l pl-4">
                                                        <div className="font-medium text-xs uppercase text-muted-foreground/80">
                                                            SALARY
                                                        </div>
                                                        {player.futureMoney.map((money) => (
                                                            <div key={money.year}>
                                                                {money.year} - {money.value === 'all'
                                                                    ? 'All'
                                                                    : `$${money.customValue}M`}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {picks.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-medium">Draft Picks</h3>
                                <div className="space-y-2">
                                    {picks.map((pick) => {
                                        // Use overall as key when available for uniqueness
                                        const key = pick.overall
                                            ? `${pick.year}-${pick.overall}`
                                            : `${pick.year}-${pick.round}`

                                        // Format pick display based on available info
                                        const pickDisplay = pick.overall
                                            ? `${pick.year} Round ${pick.round}, Pick ${pick.pick} (Overall #${pick.overall})`
                                            : `${pick.year} Round ${pick.round}`

                                        return (
                                            <div key={key} className="p-3 rounded-lg border">
                                                <div className="font-medium">{pickDisplay}</div>
                                                {pick.comment && (
                                                    <div className="mt-1 text-sm text-muted-foreground italic">
                                                        &quot;{pick.comment}&quot;
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        )
    }

    const team1Items = transformTradeOfferItems(offer.tradeOfferItems, offer.team1Id)
    const team2Items = transformTradeOfferItems(offer.tradeOfferItems, offer.team2Id)
    const isCreator = currentTeamId === offer.team1Id
    const isRecipient = currentTeamId === offer.team2Id
    const otherTeamName = isCreator ? offer.team2.teamName : offer.team1.teamName
    const canWithdraw = isCreator || adminToggle
    // Admins can accept/reject trades even if they're not the recipient
    const canAcceptReject = isRecipient || adminToggle
    // Track if admin is acting on behalf of another team
    const isAdminActingOnBehalf = adminToggle && !isRecipient && !isCreator

    // Extract all player IDs for navigation
    const allPlayers = [...team1Items.players, ...team2Items.players]
    const playerIds = allPlayers.map(player => Number(player.id)).filter(id => !isNaN(id))
    const navigationData = playerIds.length > 0 ? {
        playerIds,
        context: `trade-offer-${offer.id}` as const
    } : undefined

    return (
        <>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Badge>{offer.tradeStatus}</Badge>
                        <span className="text-sm text-muted-foreground">
                            Offered {formatDate(offer.transDate)}
                            {offer.expiresAt && (
                                <span> · Expires {formatDate(offer.expiresAt)}</span>
                            )}
                        </span>
                        {offer.messages.length > 0 && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <MessageCircle className="h-4 w-4" />
                                {offer.messages.length}
                            </div>
                        )}
                    </div>
                </div>
                {isAdminActingOnBehalf && (
                    <AdminModeBanner
                        targetTeamName={offer.team2.teamName}
                        actionType="trade"
                        className="mt-2"
                    />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderTeamSection(
                        offer.team1.teamName,
                        team1Items.players,
                        team1Items.picks,
                        navigationData
                    )}
                    {renderTeamSection(
                        offer.team2.teamName,
                        team2Items.players,
                        team2Items.picks,
                        navigationData
                    )}
                </div>
                {offer.messages.length > 0 && offer.messages.some(m => m.message?.trim()) && (
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium">Messages</h3>
                        <div className="space-y-2">
                            {offer.messages
                                .filter(message => message.message?.trim())
                                .map((message) => (
                                    <div key={message.id} className="p-3 rounded-lg border">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm font-medium">
                                                {message.fromTeamId === offer.team1Id ? offer.team1.teamName : offer.team2.teamName}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {formatDate(message.dateTime)}
                                            </div>
                                        </div>
                                        <div className="mt-1 text-sm">
                                            {message.message}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
                {offer.tradeStatus === 'Pending' && (
                    <div className="flex justify-end gap-2 mt-4">
                        {canWithdraw && (
                            <Button
                                variant="outline"
                                onClick={() => openConfirmation(offer.id, 'withdraw')}
                                className="text-foreground"
                                disabled={isWithdrawing || isAccepting || isRejecting}
                            >
                                {isWithdrawing ? 'Withdrawing...' : 'Withdraw Offer'}
                            </Button>
                        )}
                        {canAcceptReject && (
                            <Button
                                variant="destructive"
                                onClick={() => openConfirmation(offer.id, 'reject', otherTeamName)}
                                disabled={isRejecting || isAccepting || isWithdrawing}
                            >
                                {isRejecting ? 'Rejecting...' : 'Reject Offer'}
                            </Button>
                        )}
                        {canAcceptReject && (
                            <Button
                                variant="default"
                                onClick={() => openConfirmation(offer.id, 'accept', otherTeamName)}
                                disabled={isAccepting || isRejecting || isWithdrawing}
                            >
                                {isAccepting ? 'Accepting...' : 'Accept Offer'}
                            </Button>
                        )}
                    </div>
                )}
                <Separator />
            </div>

            <AlertDialog
                open={confirmationDialog.isOpen}
                onOpenChange={(isOpen) => setConfirmationDialog(prev => ({ ...prev, isOpen }))}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirmationDialog.actionType === 'accept'
                                ? 'Accept Trade Offer'
                                : confirmationDialog.actionType === 'reject'
                                    ? 'Reject Trade Offer'
                                    : 'Withdraw Trade Offer'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmationDialog.actionType === 'accept'
                                ? `Are you sure you want to accept the trade offer from ${confirmationDialog.teamName}? This action cannot be undone.`
                                : confirmationDialog.actionType === 'reject'
                                    ? `Are you sure you want to reject the trade offer from ${confirmationDialog.teamName}?`
                                    : `Are you sure you want to withdraw this trade offer? This action cannot be undone.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmation}>
                            {confirmationDialog.actionType === 'accept'
                                ? 'Accept'
                                : confirmationDialog.actionType === 'reject'
                                    ? 'Reject'
                                    : 'Withdraw'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

interface TradeOffersDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    tradeOffers: (TradeOffer & {
        tradeOfferItems: any[]
        messages: Message[]
        team1: { teamName: string }
        team2: { teamName: string }
    })[]
    currentTeamId: number
}

export function TradeOffersDialog({
    open,
    onOpenChange,
    tradeOffers,
    currentTeamId,
}: TradeOffersDialogProps) {
    const pendingOffers = tradeOffers.filter(offer => offer.tradeStatus === 'Pending')

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Pending Trade Offers</DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[calc(90vh-8rem)]">
                    <div className="py-4 space-y-6">
                        {pendingOffers.length === 0 ? (
                            <div className="text-center text-muted-foreground">
                                No pending trade offers
                            </div>
                        ) : (
                            pendingOffers.map((offer) => (
                                <TradeOfferDisplay
                                    key={offer.id}
                                    tradeOffer={offer}
                                    currentTeamId={currentTeamId}
                                />
                            ))
                        )}
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    )
} 